import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Coins, Check, AlertCircle, Loader2, ChevronDown, Wallet, Send, X, PenLine } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { prefetchBCHAddress, getCachedBCHAddress, hasBCHAddressCached } from '../hooks/useBCHAddress';
import { prefetchCommunityWalletInfo, getCachedCommunityWalletInfo, hasCommunityWalletCached, prefetchCommunityWallet } from '../hooks/useCommunityWallet';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { feeBreakdown, calculatePusdFeeSats } from '../lib/platform';
import {
  resolveTipTokens, getResolvedTipTokens, usdToTokenBaseUnits, tokenBalanceUsd,
  type TipToken,
} from '../lib/tipTokens';

// Tip currency: plain BCH ('bch') or a token, keyed by its ticker symbol.
type TipCurrency = 'bch' | string;

// BCH needed (sats) to fund a token tip: ~1000 for the token output dust
// + a miner-fee buffer. Used to gate affordability.
const TOKEN_BCH_BUFFER = BigInt(1800);

// Custom kind for BCH tip receipts (visible social proof)
const TIP_RECEIPT_KIND = 9735;

// USD-based preset amounts
const PRESET_AMOUNTS = [0.01, 0.05, 0.10, 0.25, 1, 5];

interface BCHTipButtonProps {
  recipientPubkey: string;
  recipientProfile?: any;
  eventId?: string;
  /** Where to align the dropdown. Default 'center' (above button).
   *  'right' anchors the dropdown's right edge to the button (opens left). */
  dropdownAlign?: 'center' | 'right';
}

type ButtonState = 'idle' | 'sending' | 'sent' | 'error' | 'no-funds' | 'no-bch' | 'no-address' | 'nudge-prompt' | 'nudge-sending' | 'nudge-sent';

export default function BCHTipButton({ recipientPubkey, recipientProfile, eventId, dropdownAlign = 'center' }: BCHTipButtonProps) {
  const { user, setShowLoginDialog } = useAppContext();
  const navigate = useNavigate();
  const tipWallet = useTipWallet();
  const { publish } = useNostrPublish();
  const [open, setOpen] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonState>('idle');
  const [sentAmount, setSentAmount] = useState<string>('');
  const [tipError, setTipError] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownElRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const walletInitRef = useRef(false);

  // Resolve the curated tip-token allowlist (categories, decimals, prices).
  const [tipTokens, setTipTokens] = useState<TipToken[]>(() => getResolvedTipTokens() ?? []);
  useEffect(() => {
    let alive = true;
    resolveTipTokens().then(list => { if (alive) setTipTokens(list); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const isSelf = user?.pubkey === recipientPubkey;

  // ── Hover handlers (desktop: open on hover, close on leave) ──
  const handleMouseEnter = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (!user) return;

    // Prefetch BCH address + community wallet info on hover
    if (!hasBCHAddressCached(recipientPubkey)) {
      prefetchBCHAddress(recipientPubkey);
    }
    if (!hasCommunityWalletCached(recipientPubkey)) {
      prefetchCommunityWalletInfo(recipientPubkey);
    }

    // Init wallet on first hover if not already
    if (!walletInitRef.current) {
      walletInitRef.current = true;
      tipWallet.init().catch(() => {});
    }

    setOpen(true);
  }, [user, recipientPubkey]);

  const handleMouseLeave = useCallback(() => {
    // Small delay so the dropdown doesn't flicker when moving between button and menu
    closeTimerRef.current = setTimeout(() => setOpen(false), 250);
  }, []);

  // ── Click handler (mobile: toggle) ──
  const handleButtonClick = useCallback(() => {
    // Logged out: the tip button is an invitation to log in, not a dead
    // control. Open the login dialog rather than doing nothing.
    if (!user) { setShowLoginDialog(true); return; }

    // Prefetch on click too
    if (!hasBCHAddressCached(recipientPubkey)) {
      prefetchBCHAddress(recipientPubkey);
    }
    if (!walletInitRef.current) {
      walletInitRef.current = true;
      tipWallet.init().catch(() => {});
    }

    setOpen(prev => !prev);
  }, [user, recipientPubkey]);

  // ── Close dropdown when clicking outside ──
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check both the button container and the fixed dropdown (which is outside the container)
      if (containerRef.current?.contains(target)) return;
      if (dropdownElRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Auto-clear sent/error state ──
  useEffect(() => {
    if (buttonState === 'sent' || buttonState === 'error' || buttonState === 'no-funds' || buttonState === 'no-bch' || buttonState === 'nudge-sent') {
      const t = setTimeout(() => setButtonState('idle'), buttonState === 'sent' ? 2500 : buttonState === 'no-bch' ? 4500 : 3000);
      return () => clearTimeout(t);
    }
    // "No address" briefly shows, then transitions to nudge prompt
    if (buttonState === 'no-address') {
      const t = setTimeout(() => setButtonState('nudge-prompt'), 1500);
      return () => clearTimeout(t);
    }
  }, [buttonState]);

  // ── Send nudge/invite note ──
  const handleNudge = useCallback(async () => {
    if (!user) return;
    setButtonState('nudge-sending');
    try {
      const npub = nip19.npubEncode(recipientPubkey);
      const recipientName = recipientProfile?.display_name || recipientProfile?.name || '';
      const mention = recipientName ? `@${recipientName}` : '';
      await publish({
        kind: 1,
        content: `nostr:${npub} Hey${mention ? ' ' + mention : ''}! I wanted to tip you in BCH but you don't have a BCH address set up yet. You can set one at https://bchnostr.com and start receiving tips! \uD83D\uDFE2`,
        tags: [
          ['p', recipientPubkey],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setButtonState('nudge-sent');
    } catch (err) {
      console.error('Nudge failed:', err);
      setButtonState('error');
    }
  }, [user, recipientPubkey, recipientProfile, publish]);

  // ── Send tip (with automatic retry on transient failures) ──
  const handleTip = useCallback(async (usdAmount: number, currency: TipCurrency = 'bch') => {
    setOpen(false);

    // ── Token path (PUSD, MUSD, RES, LIFT, FURU, …) ───────────────
    // The USD preset is converted to the token's base units via its live
    // price (stablecoins are $1). The recipient gets the full token amount;
    // the 1% platform fee is taken in BCH (so it doesn't vanish to token
    // decimal rounding on micro-tips). The wallet needs enough BCH for the
    // token dust + miner fee + that fee.
    if (currency !== 'bch') {
      const token = tipTokens.find(t => t.symbol === currency);
      if (!token || !token.category) {
        setTipError('Token unavailable — try again in a moment');
        setButtonState('error');
        return;
      }
      const amountBase = usdToTokenBaseUnits(usdAmount, token);
      if (amountBase <= BigInt(0)) {
        setTipError(token.priceUsd ? 'Amount too small' : `${token.symbol} price unavailable`);
        setButtonState('error');
        return;
      }
      const held = tipWallet.tokenBalances[token.category.toLowerCase()] ?? BigInt(0);
      if (held < amountBase) {
        setButtonState('no-funds');
        return;
      }
      // BCH platform fee (1% of USD value) — only collectable if we know the
      // live BCH price; otherwise send with no fee rather than block the tip.
      const bchPrice = getBCHPrice();
      const feeSats = bchPrice ? calculatePusdFeeSats(usdAmount, bchPrice) : BigInt(0);
      if (tipWallet.balanceSats < TOKEN_BCH_BUFFER + feeSats) {
        // Not a failure — the user just needs a little plain BCH to pay the
        // miner fee on a token transfer. Show a helpful "Add BCH" prompt
        // instead of a scary red "Failed" button.
        setTipError(`Sending ${token.symbol} needs a little BCH for the network fee. Add some BCH to your tip wallet.`);
        setButtonState('no-bch');
        return;
      }

      setButtonState('sending');
      setSentAmount(`$${usdAmount < 1 ? usdAmount.toFixed(2) : usdAmount} ${token.symbol}`);

      // Resolve recipient address (no split for token tips).
      let personalAddress = getCachedBCHAddress(recipientPubkey);
      if (!personalAddress) personalAddress = await prefetchBCHAddress(recipientPubkey);
      const cwInfo = hasCommunityWalletCached(recipientPubkey)
        ? getCachedCommunityWalletInfo(recipientPubkey)
        : await prefetchCommunityWalletInfo(recipientPubkey);
      const target = personalAddress || cwInfo.address;
      if (!target) {
        setButtonState('no-address');
        return;
      }

      if (!tipWallet.ready) await tipWallet.init();

      try {
        const txid = await tipWallet.sendToken(target, token.category, amountBase, { feeSats });
        setButtonState('sent');
        publish({
          kind: TIP_RECEIPT_KIND,
          content: JSON.stringify({ amount_token: Number(amountBase), amount_usd: usdAmount, currency: token.symbol, category: token.category, txid: txid || '' }),
          tags: [
            ['p', recipientPubkey],
            ...(eventId ? [['e', eventId]] : []),
            ['amount_token', amountBase.toString()],
            ['currency', token.symbol],
            ['token', token.category],
            ['t', 'token-tip'],
            ['t', 'bch-tip'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        }).catch(() => {});
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Token send failed';
        console.error('Token tip error:', msg);
        setTipError(/dust|insufficient|funds|balance/i.test(msg) ? `Not enough balance to send ${token.symbol}` : msg);
        setButtonState('error');
      }
      return;
    }

    const price = getBCHPrice();
    if (!price) {
      setTipError('BCH price unavailable — try again in a moment');
      setButtonState('error');
      return;
    }

    const bch = usdAmount / price;
    const sats = BigInt(Math.round(bch * 1e8));

    // Quick balance check before showing "Sending..."
    if (tipWallet.balanceSats < sats + BigInt(300)) {
      setButtonState('no-funds');
      return;
    }

    // ── Show "Sending..." immediately for instant feedback ──
    setButtonState('sending');
    setSentAmount(`$${usdAmount < 1 ? usdAmount.toFixed(2) : usdAmount}`);

    // ── Resolve recipient addresses + split ──────────────────────
    // Fetch community wallet info (address + split) and personal address
    const cwInfo = hasCommunityWalletCached(recipientPubkey)
      ? getCachedCommunityWalletInfo(recipientPubkey)
      : await prefetchCommunityWalletInfo(recipientPubkey);

    let personalAddress = getCachedBCHAddress(recipientPubkey);
    if (!personalAddress) {
      personalAddress = await prefetchBCHAddress(recipientPubkey);
    }

    const communityAddress = cwInfo.address;
    const splitPct = cwInfo.split; // % to community wallet

    // Determine primary address — need at least one
    const primaryAddress = communityAddress || personalAddress;
    if (!primaryAddress) {
      setButtonState('no-address');
      return;
    }

    // Calculate split: if both addresses exist and split is between 1-99,
    // do a multi-output tx. Otherwise send everything to the primary.
    const hasSplit = communityAddress && personalAddress
      && communityAddress !== personalAddress
      && splitPct > 0 && splitPct < 100;

    // Make sure wallet is ready (should be from hover init)
    if (!tipWallet.ready) {
      await tipWallet.init();
    }

    // Retry logic
    const TRANSIENT_RE = /electrum|network|timeout|ECONNRE|socket|mempool.*conflict|transaction.*already|failed to fetch|no response/i;
    const ADDRESS_RE = /cashaddr|decode|invalid.*address|checksum|non-base32|payload/i;
    const MAX_RETRIES = 1;

    let lastError = '';
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        let txid: string;

        if (hasSplit) {
          // ── Split tip: multi-output transaction ──────────────
          // Calculate split amounts BEFORE fee deduction. The fee
          // comes from the total, then the remainder is split.
          const { calculatePlatformFee, canSendFeeOutput, PLATFORM_FEE_ADDRESS } = await import('../lib/platform');
          const fee = calculatePlatformFee(Number(sats));
          const feeSats = BigInt(fee);
          const netSats = sats - feeSats;

          if (netSats <= BigInt(0)) throw new Error('Tip amount too small');

          const toCommunity = BigInt(Math.round(Number(netSats) * splitPct / 100));
          const toPersonal = netSats - toCommunity;

          const outputs: { cashaddr: string; value: bigint }[] = [];

          // Only add outputs that are above dust (546 sats)
          const DUST = BigInt(546);
          if (toCommunity >= DUST) {
            outputs.push({ cashaddr: communityAddress!, value: toCommunity });
          }
          if (toPersonal >= DUST) {
            outputs.push({ cashaddr: personalAddress!, value: toPersonal });
          }
          if (canSendFeeOutput(fee)) {
            outputs.push({ cashaddr: PLATFORM_FEE_ADDRESS, value: feeSats });
          }

          // Fallback: if split made both below dust, send all to primary
          if (outputs.length === 0 || (outputs.length === 1 && outputs[0].cashaddr === PLATFORM_FEE_ADDRESS)) {
            txid = await tipWallet.sendTipWithFee(primaryAddress, sats);
          } else {
            txid = await tipWallet.sendMultiOutput(outputs);
          }
        } else {
          // ── Single address tip (no split) ────────────────────
          txid = await tipWallet.sendTipWithFee(primaryAddress, sats);
        }
        setButtonState('sent');

        // Publish tip receipt in background (non-blocking)
        publish({
          kind: TIP_RECEIPT_KIND,
          content: JSON.stringify({ amount_sats: Number(sats), amount_usd: usdAmount, txid: txid || '' }),
          tags: [
            ['p', recipientPubkey],
            ...(eventId ? [['e', eventId]] : []),
            ['amount', Number(sats).toString()],
            ['t', 'bch-tip'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        }).catch(() => {});
        return; // Success — exit
      } catch (err) {
        lastError = err instanceof Error ? err.message : String(err);
        console.error(`Tip error (attempt ${attempt + 1}):`, lastError);

        // Address errors are permanent — don't retry
        if (ADDRESS_RE.test(lastError)) break;

        // Transient errors — retry after a short pause
        if (attempt < MAX_RETRIES && TRANSIENT_RE.test(lastError)) {
          await new Promise(r => setTimeout(r, 800));
          continue;
        }

        // Unknown error on last attempt — fall through to error state
        break;
      }
    }

    // All attempts failed
    const msg = ADDRESS_RE.test(lastError)
      ? 'This user has an invalid BCH address. They need to update it in Settings.'
      : TRANSIENT_RE.test(lastError)
        ? 'Network error — please try again'
        : lastError || 'Send failed';
    setTipError(msg);
    setButtonState('error');
  }, [recipientPubkey, eventId, tipWallet, publish, tipTokens]);

  // ── Render: button content by state ──
  const renderButton = () => {
    switch (buttonState) {
      case 'sending':
        return (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Sending...</span>
          </>
        );
      case 'sent':
        return (
          <>
            <Check className="w-3.5 h-3.5" />
            <span>Sent {sentAmount}!</span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Failed</span>
          </>
        );
      // tipError shown as title attribute below
      case 'no-funds':
        return (
          <>
            <Wallet className="w-3.5 h-3.5" />
            <span>Low balance</span>
          </>
        );
      case 'no-bch':
        return (
          <>
            <Wallet className="w-3.5 h-3.5" />
            <span>Add BCH</span>
          </>
        );
      case 'no-address':
        return (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <span>No BCH address</span>
          </>
        );
      case 'nudge-prompt':
        return (
          <>
            <Send className="w-3.5 h-3.5" />
            <span>Invite to BCH</span>
          </>
        );
      case 'nudge-sending':
        return (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Sending...</span>
          </>
        );
      case 'nudge-sent':
        return (
          <>
            <Check className="w-3.5 h-3.5" />
            <span>Invite sent!</span>
          </>
        );
      default:
        return (
          <>
            <Coins className="w-3.5 h-3.5" />
            <span>Tip</span>
            {user && <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />}
          </>
        );
    }
  };

  const buttonColorClass = (() => {
    switch (buttonState) {
      case 'sent': return 'bg-emerald-500 text-white animate-tip-glow';
      case 'sending': return 'bg-emerald-500/80 text-white';
      case 'error': return 'bg-red-500 text-white';
      case 'no-funds': return 'bg-amber-500 text-white';
      case 'no-bch': return 'bg-amber-500 text-white hover:bg-amber-600';
      case 'no-address': return 'bg-gray-500 text-white';
      case 'nudge-prompt': return 'bg-primary text-white';
      case 'nudge-sending': return 'bg-primary/80 text-white';
      case 'nudge-sent': return 'bg-primary text-white';
      default: return 'bg-emerald-500 text-white hover:bg-emerald-600';
    }
  })();

  const price = getBCHPrice();
  const walletBalanceUSD = price ? Number(tipWallet.balanceSats) / 1e8 * price : 0;

  // Don't show tip button on own posts — placed after all hooks to respect Rules of Hooks
  if (isSelf) return null;

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main tip button */}
      <button
        onClick={
          buttonState === 'nudge-prompt' ? handleNudge
          : buttonState === 'no-bch' ? () => navigate('/settings?tab=wallet')
          : handleButtonClick
        }
        disabled={buttonState === 'sending' || buttonState === 'nudge-sending'}
        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all active:scale-95 disabled:opacity-40 disabled:hover:scale-100 ${buttonColorClass}`}
        title={
          (buttonState === 'error' || buttonState === 'no-bch') && tipError ? tipError
          : buttonState === 'nudge-prompt' ? 'Invite this user to set up BCH'
          : !user ? 'Log in to tip BCH'
          : 'Tip BCH'
        }
      >
        {renderButton()}
      </button>

      {/* Dropdown */}
      {open && buttonState === 'idle' && user && (
        <TipDropdown
          containerRef={containerRef}
          dropdownElRef={dropdownElRef}
          dropdownAlign={dropdownAlign}
          tipWallet={tipWallet}
          walletBalanceUSD={walletBalanceUSD}
          price={price}
          tipTokens={tipTokens}
          onTip={handleTip}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {/* Nudge/Invite prompt — shown when recipient has no BCH address */}
      {buttonState === 'nudge-prompt' && user && (
        <NudgePrompt
          containerRef={containerRef}
          dropdownAlign={dropdownAlign}
          onNudge={handleNudge}
          onDismiss={() => setButtonState('idle')}
        />
      )}
    </div>
  );
}

// ── Nudge prompt — portaled to body for correct positioning ──
function NudgePrompt({
  containerRef,
  dropdownAlign,
  onNudge,
  onDismiss,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  dropdownAlign: 'center' | 'right';
  onNudge: () => void;
  onDismiss: () => void;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const nudgeH = 140; // approximate height
    const top = rect.top - nudgeH - 8;
    let left: number;
    if (dropdownAlign === 'right') {
      left = rect.right - 220;
    } else {
      left = rect.left + rect.width / 2 - 110; // center (220/2)
    }
    left = Math.max(8, Math.min(left, window.innerWidth - 228));
    setPos({ top: Math.max(4, top), left });
  }, [dropdownAlign, containerRef]);

  if (!pos) return null;

  return createPortal(
    <div
      className="fixed z-[100]"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="bg-card border border-border rounded-xl shadow-xl overflow-hidden w-[220px]">
        <div className="px-3 py-2.5 border-b border-border bg-amber-500/5">
          <p className="text-[11px] font-medium text-foreground">No BCH address found</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Let them know you want to tip!</p>
        </div>
        <div className="p-2 space-y-1.5">
          <button
            onClick={onNudge}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            Invite to BCH
          </button>
          <button
            onClick={onDismiss}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-muted transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Smart dropdown — uses fixed positioning & portal to escape overflow containers ──
// Measures the button's screen position and places the dropdown in the
// optimal direction (above or below). Rendered into document.body via portal
// to escape `contain: layout`, `overflow: hidden`, and stacking contexts.
function TipDropdown({
  containerRef,
  dropdownElRef,
  dropdownAlign,
  tipWallet,
  walletBalanceUSD,
  price,
  tipTokens,
  onTip,
  onMouseEnter,
  onMouseLeave,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  dropdownElRef: React.MutableRefObject<HTMLDivElement | null>;
  dropdownAlign: 'center' | 'right';
  tipWallet: any;
  walletBalanceUSD: number;
  price: number | null;
  tipTokens: TipToken[];
  onTip: (usd: number, currency: TipCurrency) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const localRef = useRef<HTMLDivElement>(null);

  const tokenBalances: Record<string, bigint> = tipWallet.tokenBalances ?? {};

  // Which allowlisted tokens does the wallet actually hold (resolved + >0)?
  const heldTokens = tipTokens.filter(
    t => t.category && (tokenBalances[t.category.toLowerCase()] ?? BigInt(0)) > BigInt(0),
  );
  // Allowlisted tokens the user does NOT hold — offered as "get it" links.
  const acquirableTokens = tipTokens.filter(
    t => t.category && (tokenBalances[t.category.toLowerCase()] ?? BigInt(0)) <= BigInt(0),
  );

  // Selected currency: 'bch' or a held token's symbol.
  const [currency, setCurrency] = useState<TipCurrency>('bch');
  const selectedToken = currency !== 'bch' ? tipTokens.find(t => t.symbol === currency) ?? null : null;
  // If the selected token is no longer held (spent it all), fall back to BCH.
  useEffect(() => {
    if (currency !== 'bch' && !heldTokens.some(t => t.symbol === currency)) setCurrency('bch');
  }, [currency, heldTokens]);

  const setLocalRef = useCallback((el: HTMLDivElement | null) => {
    (localRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    dropdownElRef.current = el;
  }, [dropdownElRef]);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownEl = localRef.current;
    const dropdownH = dropdownEl ? dropdownEl.offsetHeight : 280;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openBelow = spaceAbove < dropdownH + 8 && spaceBelow > spaceAbove;
    const top = openBelow ? rect.bottom + 6 : rect.top - dropdownH - 6;
    let left: number;
    if (dropdownAlign === 'right') {
      left = rect.right - 200; // 200 = dropdown width
    } else {
      left = rect.left + rect.width / 2 - 100; // center
    }
    left = Math.max(8, Math.min(left, window.innerWidth - 208));
    setPos({ top: Math.max(4, top), left });
  }, [dropdownAlign, containerRef]);

  useEffect(() => {
    updatePosition();
    requestAnimationFrame(updatePosition);

    // Keep the dropdown glued to the button while it's open. A playing/loading
    // video (or any late-arriving media) reflows the feed, which moves the
    // button; without this the fixed-position dropdown is left stranded away
    // from the cursor, the mouse "leaves" it, and it closes almost instantly.
    // We follow scroll, resize, and any layout change around the button.
    const onChange = () => updatePosition();
    window.addEventListener('scroll', onChange, true); // capture: catch scroll in any ancestor
    window.addEventListener('resize', onChange);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onChange);
      // Observe the document body so feed reflows (video sizing, images
      // loading) reposition the dropdown too, not just button-size changes.
      if (document.body) ro.observe(document.body);
      if (containerRef.current) ro.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      ro?.disconnect();
    };
  }, [updatePosition, containerRef]);

  if (!pos) return null;

  // Selected token's held balance (base units) + a human balance string.
  const selBal = selectedToken?.category ? (tokenBalances[selectedToken.category.toLowerCase()] ?? BigInt(0)) : BigInt(0);
  const selBalUsd = selectedToken ? tokenBalanceUsd(selBal, selectedToken) : null;
  const needsBch = !!selectedToken && tipWallet.balanceSats < TOKEN_BCH_BUFFER;

  return createPortal(
    <div
      ref={setLocalRef}
      className="fixed z-[100] tip-dropdown-enter"
      style={{ top: pos.top, left: pos.left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="bg-card border border-border rounded-xl shadow-xl overflow-hidden w-[200px]">
        {/* Currency selector — BCH + each held token */}
        {heldTokens.length > 0 && (
          <div className="flex flex-wrap gap-0.5 p-1 border-b border-border bg-muted/30">
            <button
              onClick={() => setCurrency('bch')}
              className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${currency === 'bch' ? 'bg-emerald-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              BCH
            </button>
            {heldTokens.map(t => (
              <button
                key={t.symbol}
                onClick={() => setCurrency(t.symbol)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${currency === t.symbol ? 'bg-emerald-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t.iconUrl && <img src={t.iconUrl} alt="" className="w-3 h-3 rounded-full" loading="lazy" />}
                {t.symbol}
              </button>
            ))}
          </div>
        )}

        {/* Balance header */}
        <div className="px-3 py-2 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Wallet className="w-3 h-3" /> {selectedToken ? selectedToken.symbol : 'BCH'} Balance
            </span>
            <span className="text-[10px] font-mono font-semibold">
              {selectedToken
                ? (selBalUsd != null ? `$${selBalUsd.toFixed(2)}` : `${Number(selBal)}`)
                : tipWallet.loading ? '...' : walletBalanceUSD > 0 ? `$${walletBalanceUSD.toFixed(2)}` : formatSats(tipWallet.balanceSats)}
            </span>
          </div>
        </div>

        {/* Amount options (USD) */}
        <div className="py-1">
          {PRESET_AMOUNTS.map((amount) => {
            let canAfford: boolean;
            if (selectedToken) {
              const need = usdToTokenBaseUnits(amount, selectedToken);
              canAfford = need > BigInt(0) && selBal >= need && tipWallet.balanceSats >= TOKEN_BCH_BUFFER;
            } else {
              const sats = price ? Math.round((amount / price) * 1e8) : 0;
              canAfford = tipWallet.balanceSats >= BigInt(sats) + BigInt(300);
            }
            return (
              <button
                key={amount}
                onClick={() => onTip(amount, currency)}
                disabled={!tipWallet.ready || !canAfford}
                className={`w-full flex items-center px-3 py-1.5 text-xs transition-colors ${
                  canAfford && tipWallet.ready
                    ? 'hover:bg-emerald-500/10 active:bg-emerald-500/20 cursor-pointer'
                    : 'opacity-40 cursor-not-allowed'
                }`}
              >
                <span className="font-semibold font-mono text-emerald-500">
                  ${amount < 1 ? amount.toFixed(2) : amount}
                  {selectedToken && <span className="text-[9px] text-muted-foreground ml-1">{selectedToken.symbol}</span>}
                </span>
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <CustomAmountInput
          onSend={(amount) => onTip(amount, currency)}
          disabled={!tipWallet.ready}
          walletBalanceSats={tipWallet.balanceSats}
          bchPrice={price}
          token={selectedToken}
          tokenBalanceBase={selBal}
        />
        {needsBch && (
          <p className="text-[9px] text-amber-500 px-3 pb-1.5 leading-tight">
            Add a little BCH to cover the token network fee.
          </p>
        )}

        {/* Acquire links — tokens the user doesn't hold yet */}
        {acquirableTokens.length > 0 && (
          <div className="border-t border-border px-2 py-1.5">
            <p className="text-[9px] text-muted-foreground mb-1 px-1">Get more tokens to tip:</p>
            <div className="flex flex-wrap gap-1">
              {acquirableTokens.map(t => (
                <a
                  key={t.symbol}
                  href={t.swapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-navigate flex items-center gap-1 px-1.5 py-0.5 rounded border border-border text-[9px] text-muted-foreground hover:text-foreground hover:border-emerald-500/50 transition-colors"
                >
                  {t.iconUrl && <img src={t.iconUrl} alt="" className="w-2.5 h-2.5 rounded-full" loading="lazy" />}
                  {t.symbol}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function CustomAmountInput({
  onSend,
  disabled,
  walletBalanceSats,
  bchPrice,
  token = null,
  tokenBalanceBase = BigInt(0),
}: {
  onSend: (usd: number) => void;
  disabled: boolean;
  walletBalanceSats: bigint;
  bchPrice: number | null;
  token?: TipToken | null;
  tokenBalanceBase?: bigint;
}) {
  const [showInput, setShowInput] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showInput) inputRef.current?.focus();
  }, [showInput]);

  const parsedAmount = parseFloat(value);
  const isValid = !isNaN(parsedAmount) && parsedAmount >= 0.01;
  const canAfford = token
    ? (isValid
        ? (() => { const need = usdToTokenBaseUnits(parsedAmount, token); return need > BigInt(0) && tokenBalanceBase >= need && walletBalanceSats >= TOKEN_BCH_BUFFER; })()
        : false)
    : walletBalanceSats >= BigInt(isValid && bchPrice ? Math.round((parsedAmount / bchPrice) * 1e8) : 0) + BigInt(300);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || disabled || !canAfford) return;
    onSend(parsedAmount);
    setValue('');
    setShowInput(false);
  };

  if (!showInput) {
    return (
      <div className="border-t border-border">
        <button
          onClick={() => setShowInput(true)}
          className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <PenLine className="w-3 h-3" /> Custom amount
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-border px-2 py-1.5">
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-muted-foreground font-bold shrink-0">$</span>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0.01"
          placeholder="0.00"
          value={value}
          onChange={e => setValue(e.target.value)}
          className="w-full bg-muted rounded px-1.5 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <button
          type="submit"
          disabled={disabled || !isValid || !canAfford}
          className="shrink-0 px-2 py-1 rounded bg-emerald-500 text-white text-[10px] font-bold hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Send
        </button>
      </div>
      {value && isValid && !canAfford && (
        <p className="text-[9px] text-amber-500 mt-0.5 px-0.5">Insufficient balance</p>
      )}
    </form>
  );
}

function formatSats(sats: bigint): string {
  const bch = Number(sats) / 1e8;
  if (bch === 0) return '0 BCH';
  if (bch < 0.001) return `${Number(sats).toLocaleString()} sats`;
  return `${bch.toFixed(bch < 0.01 ? 6 : 4)} BCH`;
}

